import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface TimerProps {
  timeRemaining: number; // in seconds
  onTimeUp: () => void;
  onTick: (timeLeft: number) => void;
}

export const Timer: React.FC<TimerProps> = ({ timeRemaining, onTimeUp, onTick }) => {
  const [timeLeft, setTimeLeft] = useState(timeRemaining);

  useEffect(() => {
    setTimeLeft(timeRemaining);
  }, [timeRemaining]);

  useEffect(() => {
    if (timeLeft <= 0) {
      onTimeUp();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;
        onTick(newTime);
        if (newTime <= 0) {
          onTimeUp();
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onTimeUp, onTick]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = (): string => {
    if (timeLeft <= 10) return 'text-red-600 bg-red-100 border-red-300';
    if (timeLeft <= 30) return 'text-orange-600 bg-orange-100 border-orange-300';
    return 'text-green-600 bg-green-100 border-green-300';
  };

  return (
    <motion.div 
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 shadow-md font-bold ${getTimerColor()}`}
      initial={{ scale: 0.9 }}
      animate={{ 
        scale: timeLeft <= 10 ? [1, 1.05, 1] : 1,
      }}
      transition={{ 
        duration: timeLeft <= 10 ? 0.5 : 0,
        repeat: timeLeft <= 10 ? Infinity : 0 
      }}
    >
      <span className="text-2xl">⏱️</span>
      <span className="text-lg font-mono">{formatTime(timeLeft)}</span>
    </motion.div>
  );
}; 